/**
 * Cookie / tracking consent — single source of truth.
 *
 * Reads & writes the GDPR-compliant consent flags in localStorage and emits
 * a 'consent-changed' event so listeners (Meta Pixel loader) react in real time.
 *
 * Storage shape (versioned to allow re-prompting if categories change):
 *   localStorage["hca_cookie_consent_v2"] = JSON.stringify({
 *     necessary: true,            // always true
 *     marketing: true | false,    // Meta Pixel + similar
 *     timestamp: "2026-…"
 *   })
 */

export const STORAGE_KEY = 'hca_cookie_consent_v2';
export const LEGACY_KEY = 'hca_cookie_consent_v1';
const EVT = 'hca:consent-changed';

export function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      necessary: true,
      marketing: !!parsed.marketing,
      timestamp: parsed.timestamp,
    };
  } catch { return null; }
}

export function writeConsent({ marketing }) {
  const payload = {
    necessary: true,
    marketing: !!marketing,
    timestamp: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Clear the legacy v1 key so the new banner is the source of truth.
    localStorage.removeItem(LEGACY_KEY);
  } catch { /* ignore quota / privacy mode */ }
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: payload }));
  } catch { /* ignore */ }
  // GDPR — tell Google Consent Mode + GTM about the user's choice so any
  // GA4 / Ads / Meta tag configured inside the GTM container can react.
  // Defaults in index.html start as "denied" → we only switch to "granted"
  // here, after the explicit click on the consent banner.
  try {
    window.dataLayer = window.dataLayer || [];
    const grant = marketing ? 'granted' : 'denied';
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: grant,
        ad_user_data: grant,
        ad_personalization: grant,
        analytics_storage: grant,
      });
    } else {
      // Fallback when gtag() helper isn't on the page (shouldn't happen
      // because we install it in index.html, but stay defensive).
      window.dataLayer.push(['consent', 'update', {
        ad_storage: grant,
        ad_user_data: grant,
        ad_personalization: grant,
        analytics_storage: grant,
      }]);
    }
    // Custom event so GTM triggers can fire "after the user decided".
    window.dataLayer.push({
      event: marketing ? 'consent_granted' : 'consent_denied',
      consent_marketing: !!marketing,
    });
  } catch { /* ignore */ }
  return payload;
}

export function onConsentChanged(handler) {
  window.addEventListener(EVT, handler);
  return () => window.removeEventListener(EVT, handler);
}

/** True if the user has actively saved a choice (banner not currently needed). */
export function hasDecided() {
  return readConsent() != null;
}

/** True if the user has opted in to marketing/Pixel tracking. */
export function marketingAllowed() {
  return !!readConsent()?.marketing;
}
